import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { fetchPublicNewsById, type PublicNewsItem } from '@/features/public/api';
import { useRealtimeInvalidation } from '@/shared/realtime/useRealtimeInvalidation';

const fallbackImage =
  "https://readdy.ai/api/search-image?query=senegal%20africa%20news%20article%20professional%20photography%20editorial%20clean%20background&width=1200&height=600&seq=news-detail&orientation=landscape";

export default function NewsDetailPage() {
  const { id } = useParams();
  const [article, setArticle] = useState<PublicNewsItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadArticle = useCallback(async () => {
    if (!id) {
      setError("Identifiant d'article manquant.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await fetchPublicNewsById(id);
      setArticle(data);
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Impossible de charger l'article.";
      setError(message);
      setArticle(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void loadArticle();
  }, [loadArticle]);

  useRealtimeInvalidation({
    topics: ["news"],
    enabled: Boolean(id),
    onInvalidate: () => {
      void loadArticle();
    }
  });

  const paragraphes = article?.contenu
    ? article.contenu.split(/\n+/).map((line) => line.trim()).filter(Boolean)
    : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <section className="relative py-12 overflow-hidden">
        <div className="absolute inset-0 bg-black">
          <img
            src={article?.image ?? fallbackImage}
            alt={article?.titre ?? "Actualité ASC"}
            className="w-full h-full object-contain"
          />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative text-white">
          <div className="space-y-4">
            <span className="inline-flex items-center gap-2 bg-orange-500/90 text-white text-sm font-medium px-4 py-1.5 rounded-full">
              {article?.categorie ?? "Actualité"}
            </span>
            <h1 className="text-3xl md:text-4xl font-bold">{article?.titre ?? "Chargement..."}</h1>
            {article?.date && (
              <p className="text-white/80 text-sm">
                Publié le {article.date}
              </p>
            )}
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="space-y-4">
            <div className="h-6 w-40 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-4 w-full bg-gray-100 rounded animate-pulse"></div>
            <div className="h-4 w-5/6 bg-gray-100 rounded animate-pulse"></div>
            <div className="h-4 w-4/6 bg-gray-100 rounded animate-pulse"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-100 text-red-700 rounded-xl p-5 text-sm">
            {error}
          </div>
        ) : article ? (
          <article className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8">
            <div className="mb-8 overflow-hidden rounded-[28px] border border-gray-100 bg-[#111827]">
              <img
                src={article.image ?? fallbackImage}
                alt={article.titre}
                className="w-full max-h-[620px] object-contain bg-[#111827]"
              />
            </div>
            {paragraphes.length ? (
              <div className="space-y-5 break-words text-gray-700">
                {paragraphes.map((line, index) => (
                  <p key={index} className="whitespace-pre-wrap text-base leading-8 md:text-lg">
                    {line}
                  </p>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">Aucun contenu détaillé pour cet article.</p>
            )}
          </article>
        ) : (
          <div className="text-center text-gray-500">Article introuvable.</div>
        )}
      </div>
      <Footer />
    </div>
  );
}
